import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import axios from 'axios';

const API_BASE_HTTPS = process.env.REACT_APP_API_URL || 'https://localhost:7188/api';
const API_BASE_HTTP  = API_BASE_HTTPS.replace('https://', 'http://');

/** Axios call with https→http fallback (no auth needed for public routes) */
async function publicGet(url) {
  try {
    return await axios.get(API_BASE_HTTPS + url);
  } catch (e) {
    if (e.code === 'ERR_NETWORK' || e.code === 'ECONNREFUSED') {
      return axios.get(API_BASE_HTTP + url);
    }
    throw e;
  }
}
async function publicPost(url, data) {
  try {
    return await axios.post(API_BASE_HTTPS + url, data);
  } catch (e) {
    if (e.code === 'ERR_NETWORK' || e.code === 'ECONNREFUSED') {
      return axios.post(API_BASE_HTTP + url, data);
    }
    throw e;
  }
}

// ── Thunks ────────────────────────────────────────────────────────────────────

/** Generate a client signing link for a contrat (authenticated) */
export const genererLienSignature = createAsyncThunk(
  'signature/genererLien',
  async (contratId, { rejectWithValue }) => {
    try {
      const r = await api.post(`/Signature/contrat/${contratId}/generer-lien`);
      // Backend returns API-origin URL — rebuild with frontend origin so QR/link works
      const token = r.data.lien?.split('/sign/').pop();
      const frontendLien = `${window.location.origin}/sign/${token}`;
      return { contratId, lien: frontendLien, expiry: r.data.expiry };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message);
    }
  }
);

/** Get contrat info by public token (no auth) */
export const getSignatureByToken = createAsyncThunk(
  'signature/getByToken',
  async (token, { rejectWithValue }) => {
    try {
      const r = await publicGet(`/Signature/${token}`);
      return r.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message || 'Erreur réseau');
    }
  }
);

/** Submit client signature (no auth) */
export const soumettrSignature = createAsyncThunk(
  'signature/signer',
  async ({ token, signatureBase64, methode }, { rejectWithValue }) => {
    try {
      const r = await publicPost(`/Signature/${token}`, { signatureBase64, methode: methode || 'lien' });
      return r.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || e.message || 'Erreur réseau');
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const signatureSlice = createSlice({
  name: 'signature',
  initialState: {
    // For public signing page
    contratPublic: null,    // SignaturePublicDto
    loadingPublic: false,
    errorPublic: null,
    signed: false,          // successfully signed

    // For generating a link (owner side)
    lienEnCours: null,      // { contratId, lien, expiry }
    loadingLien: false,
    errorLien: null,
  },
  reducers: {
    clearSignatureState: (state) => {
      state.contratPublic = null;
      state.loadingPublic = false;
      state.errorPublic = null;
      state.signed = false;
    },
    clearLien: (state) => {
      state.lienEnCours = null;
      state.errorLien = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // genererLienSignature
      .addCase(genererLienSignature.pending, (state) => { state.loadingLien = true; state.errorLien = null; })
      .addCase(genererLienSignature.fulfilled, (state, action) => {
        state.loadingLien = false;
        state.lienEnCours = action.payload;
      })
      .addCase(genererLienSignature.rejected, (state, action) => {
        state.loadingLien = false;
        state.errorLien = action.payload;
      })

      // getSignatureByToken
      .addCase(getSignatureByToken.pending, (state) => { state.loadingPublic = true; state.errorPublic = null; })
      .addCase(getSignatureByToken.fulfilled, (state, action) => {
        state.loadingPublic = false;
        state.contratPublic = action.payload;
      })
      .addCase(getSignatureByToken.rejected, (state, action) => {
        state.loadingPublic = false;
        state.errorPublic = action.payload;
      })

      // soumettrSignature
      .addCase(soumettrSignature.pending, (state) => { state.loadingPublic = true; state.errorPublic = null; })
      .addCase(soumettrSignature.fulfilled, (state) => {
        state.loadingPublic = false;
        state.signed = true;
        if (state.contratPublic) state.contratPublic.estSigne = true;
      })
      .addCase(soumettrSignature.rejected, (state, action) => {
        state.loadingPublic = false;
        state.errorPublic = action.payload;
      });
  },
});

export const { clearSignatureState, clearLien } = signatureSlice.actions;
export default signatureSlice.reducer;
